import React, { useState, useEffect } from 'react';
import { Button, Slider, Tooltip } from 'antd';
import { ArrowRightOutlined, ReloadOutlined } from '@ant-design/icons';
import { MathsFactory } from '../MathsFactory';

/**
 * VisualFeedbackWidget
 * "Time Travel" metaphor to toggle/scrub between answer and correction.
 */
export const VisualFeedbackWidget = ({ config }) => {
    const { steps = [] } = config;
    const [index, setIndex] = useState(0);
    const [viewMode, setViewMode] = useState('BEFORE'); // 'BEFORE' | 'AFTER'

    if (!steps.length) return null;

    const step = steps[index];

    // FIX: Pass the specific config for the state, not empty object
    // Many widgets (NumberLine, ProbabilityTree) rely on config for structure
    const activeConfig = (viewMode === 'BEFORE' ? step.before.config : step.after.config) || {};

    // FIX: Guard against undefined variables
    const activeVariableState = (viewMode === 'BEFORE' ? step.before.variables : step.after.variables) || {};

    const activeWidgetType = viewMode === 'BEFORE' ? step.before.widget : step.after.widget;

    useEffect(() => {
        setViewMode('BEFORE');
    }, [index]);

    const isCorrect = step.feedback === 'CORRECT';

    return (
        <div className="flexColumn gap-medium width-100">
            {/* 1. HERO STAGE */}
            <div className={`ch-emph-block relative ${isCorrect ? 'ch-glow-success' : ''}`} style={{ minHeight: 320, padding: 0, overflow: 'hidden' }}>

                {/* Context Label */}
                <div className="absolute top-0 left-0 right-0 padding-x--m padding-y--s flexSpaceBetween z-10" style={{ background: 'rgba(255,255,255,0.9)', borderBottom: '1px solid #f0f0f0' }}>
                    <span className="ch-text-bold ch-text-small uppercase tracking-wide text-muted">
                        {step.shortLabel || "Comparison Mode"}
                    </span>
                    <span className={`ch-badge ${viewMode === 'AFTER' ? 'ch-badge-success' : 'ch-badge-warning'}`}>
                        {viewMode === 'BEFORE' ? 'Your Answer' : 'Correction'}
                    </span>
                </div>

                {/* The Widget */}
                <div className="padding-t--xl padding-x--m padding-b--m h-full flexAlignCentre justify-center">
                    <div style={{ width: '100%', maxWidth: '500px' }}>
                        <MathsFactory
                            type={activeWidgetType}
                            config={activeConfig}
                            variables={activeVariableState}
                        />
                    </div>
                </div>

                {/* Overlay Toggle */}
                <div className="absolute bottom-4 right-4 z-20">
                    <Tooltip title="Toggle to see the correction">
                        <Button
                            type={viewMode === 'AFTER' ? 'primary' : 'default'}
                            shape="round"
                            size="large"
                            icon={viewMode === 'BEFORE' ? <ArrowRightOutlined /> : <ReloadOutlined />}
                            onClick={() => setViewMode(prev => prev === 'BEFORE' ? 'AFTER' : 'BEFORE')}
                            className="shadow-md"
                        >
                            {viewMode === 'BEFORE' ? 'See Fix' : 'Replay Error'}
                        </Button>
                    </Tooltip>
                </div>
            </div>

            {/* 2. NARRATIVE CONTROLS */}
            {steps.length > 1 && (
                <div className="ch-card padding--m bg-white border-all--s">
                    <div className="flexAlignCentre gap-medium">
                        <Button disabled={index === 0} onClick={() => setIndex(i => i - 1)}>
                            Previous
                        </Button>
                        <div className="flex1">
                            <Slider
                                min={0} max={steps.length - 1} value={index} onChange={setIndex}
                                tooltip={{ formatter: v => `Scenario ${v + 1}` }}
                            />
                        </div>
                        <Button disabled={index === steps.length - 1} onClick={() => setIndex(i => i + 1)}>
                            Next
                        </Button>
                    </div>
                    <div className="text-center margin-t--s ch-text-small text-muted">
                        Scenario {index + 1} of {steps.length}
                    </div>
                </div>
            )}
        </div>
    );
};